import { useEffect, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { mirrorAuthIdentity, sameMirrorAuthIdentity, type MirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import { useMirrorLive, type MirrorLiveAuthority, type UseMirrorLiveOptions, type UseMirrorLiveResult } from "./useMirrorLive";

/** Production entry point. The media core receives explicit authority rather
 * than inferring a user from an ambient function invocation. */
export function useAuthenticatedMirrorLive(options: Omit<UseMirrorLiveOptions, "authority">): UseMirrorLiveResult {
  const source = useRef({
    identity: null as MirrorAuthIdentity | null,
    revision: 0,
    generation: 0,
    mounted: false,
    listeners: new Set<() => void>(),
  });

  const authority = useMemo<MirrorLiveAuthority>(() => ({
    acquire() {
      const identity = source.current.identity;
      if (!source.current.mounted || !identity) return null;
      const generation = source.current.generation;
      const current = () => source.current.generation === generation &&
        sameMirrorAuthIdentity(identity, source.current.identity);
      return {
        ownerId: identity.ownerId,
        authSessionId: identity.authSessionId,
        originalToken: identity.accessToken,
        isCurrent: current,
        token: () => current() ? source.current.identity!.accessToken : null,
        acceptsSession: session => current() && sameMirrorAuthIdentity(identity, mirrorAuthIdentity(session)),
      };
    },
    subscribeInvalidation(cancel) {
      source.current.listeners.add(cancel);
      return () => { source.current.listeners.delete(cancel); };
    },
  }), []);

  useEffect(() => {
    const state = source.current;
    state.mounted = true;
    const update = (session: unknown) => {
      const identity = mirrorAuthIdentity(session);
      const changed = !sameMirrorAuthIdentity(state.identity, identity);
      state.identity = identity;
      if (changed) {
        state.generation += 1;
        for (const cancel of state.listeners) cancel();
      }
    };
    // Supabase holds its auth lock while notifying subscribers. This callback
    // only updates local authority and synchronously disposes media; the core
    // defers captured-session network cleanup until the callback has returned.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      state.revision += 1;
      update(session);
    });
    const revision = state.revision;
    void supabase.auth.getSession().then(result => {
      if (state.mounted && state.revision === revision) update(result.error ? null : result.data.session);
    }).catch(() => {
      if (state.mounted && state.revision === revision) update(null);
    });
    return () => {
      state.mounted = false;
      state.revision += 1;
      subscription.unsubscribe();
      // Ordinary navigation keeps the captured identity only for the core's
      // bounded final transcript drain and original-session cleanup.
    };
  }, []);

  return useMirrorLive({ ...options, authority });
}
